import React, { useEffect, useState } from "react";
import AdminLayout from "../../layout/AdminLayout";
import { useParams } from "react-router-dom";
import { getRestaurantById } from "../../../api/restaurants.api";
import { baseURL } from "../../../api/api";
import { Link } from "react-router-dom";
import {
  FiPlus,
  FiMonitor,
  FiClock,
  FiPhone,
  FiMail,
  FiMapPin,
  FiUsers,
} from "react-icons/fi";
import { RotatingLines } from "react-loader-spinner";
import { FaCampground, FaCircle } from "react-icons/fa";

function RestaurantDetails() {
  const { id } = useParams();
  const [restaurantDetails, setRestaurantDetails] = useState(null);
  const [currentImage, setCurrentImage] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");

  useEffect(() => {
    getRestaurantById(id)
      .then((res) => {
        setRestaurantDetails(res?.data);
        if (res?.data?.images?.length > 0) {
          setCurrentImage(res?.data?.images[0]);
        }
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  const renderOpeningHours = (hours) => {
    const days = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];

    return days.map((day) => (
      <div
        key={day}
        className="flex items-center justify-between py-2 px-4 border-b border-gray-100 last:border-0"
      >
        <span className="font-medium text-gray-700 capitalize">{day}</span>
        <span className="text-gray-600">
          {hours[day].start && hours[day].end ? (
            `${hours[day].start} - ${hours[day].end}`
          ) : (
            <span className="text-gray-400">Closed</span>
          )}
        </span>
      </div>
    ));
  };

  const handleImageClick = (image) => {
    setCurrentImage(image);
  };

  const getImageUrl = (imageName) => {
    return `${baseURL}/s3/images/${imageName}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
        return "bg-green-100 text-green-800";
      case "Offline":
        return "bg-red-100 text-red-800";
      case "Pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <AdminLayout title="Fixed Location Details">
      {loading ? (
        <div className="flex justify-center items-center h-screen">
          <RotatingLines
            strokeColor="black"
            strokeWidth="5"
            animationDuration="0.75"
            width="96"
            visible={true}
          />
        </div>
      ) : (
        <>
          {/* Header with Edit Button */}
          <div className="flex items-center justify-between sticky top-16 bg-white py-4 px-6 z-10 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-800">
              {restaurantDetails?.name}
            </h1>
            <Link
              to={`/fixedLocations/edit/${id}`}
              className="flex items-center justify-center rounded-md bg-yellow-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-yellow-600 transition-colors"
            >
              <FiPlus className="mr-2" />
              Edit Fixed Location
            </Link>
          </div>

          {/* Navigation Tabs */}
          <div className="px-6 pt-4 border-b border-gray-200">
            <nav className="flex space-x-8">
              <button
                onClick={() => setActiveTab("details")}
                className={`px-1 py-3 text-sm font-medium border-b-2 ${
                  activeTab === "details"
                    ? "border-yellow-500 text-yellow-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab("screens")}
                className={`px-1 py-3 text-sm font-medium border-b-2 ${
                  activeTab === "screens"
                    ? "border-yellow-500 text-yellow-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Screens ({restaurantDetails?.screens?.length || 0})
              </button>
            </nav>
          </div>

          {/* Main Content */}
          <div className="px-6 py-4">
            {activeTab === "details" ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column - Restaurant Info */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Image Gallery */}
                  {restaurantDetails?.images?.length > 0 && (
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                      <div className="h-64 md:h-80 w-full bg-gray-100 flex items-center justify-center">
                        <img
                          src={currentImage ? getImageUrl(currentImage) : ""}
                          alt="Restaurant"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-4 flex space-x-2 overflow-x-auto">
                        {restaurantDetails?.images?.map((image, index) => (
                          <button
                            key={index}
                            onClick={() => handleImageClick(image)}
                            className={`w-16 h-16 flex-shrink-0 rounded-md overflow-hidden ${
                              currentImage === image
                                ? "ring-2 ring-yellow-500"
                                : ""
                            }`}
                          >
                            <img
                              src={getImageUrl(image)}
                              alt={`Thumbnail ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Restaurant Details Card */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <FiMapPin className="mr-2 text-yellow-500" />
                      Fixed Location Information
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">
                          Cuisine Type
                        </h3>
                        <p className="mt-1 text-gray-800">
                          {restaurantDetails?.cuisineType}
                        </p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">
                          Location
                        </h3>
                        <p className="mt-1 text-gray-800">
                          {restaurantDetails?.location}
                        </p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">
                          Seating Capacity
                        </h3>
                        <p className="mt-1 text-gray-800">
                          {restaurantDetails?.seatingCapacity}
                        </p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">
                          Foot Traffic
                        </h3>
                        <p className="mt-1 text-gray-800">
                          {restaurantDetails?.footTrafficEstimate?.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">
                          Price Per Week
                        </h3>
                        <p className="mt-1 text-gray-800">
                          {restaurantDetails?.basePricePerWeek?.toLocaleString()}{" "}
                          €
                        </p>
                      </div>
                    </div>

                    {restaurantDetails?.description && (
                      <div className="mt-6">
                        <h3 className="text-sm font-medium text-gray-500">
                          Description
                        </h3>
                        <p className="mt-1 text-gray-800">
                          {restaurantDetails?.description}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Opening Hours Card */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <FiClock className="mr-2 text-yellow-500" />
                      Opening Hours
                    </h2>
                    <div className="divide-y divide-gray-100">
                      {renderOpeningHours(
                        restaurantDetails?.openingHours || {}
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Column - Contact Info */}
                <div className="space-y-6">
                  {/* Status Card */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <FaCircle
                        className={`mr-2 text-${
                          restaurantDetails?.status === "Approved"
                            ? "green"
                            : "red"
                        }-500`}
                      />
                      Status
                    </h2>
                    <div className="flex items-center justify-between">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          restaurantDetails?.status === "Approved"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {restaurantDetails?.status}
                      </span>
                      <span className="text-sm text-gray-500">
                        Updated:{" "}
                        {new Date(
                          restaurantDetails?.updatedAt
                        ).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {/* Contact Card */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <FiUsers className="mr-2 text-yellow-500" />
                      Contact Information
                    </h2>

                    <div className="space-y-4">
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 flex items-center">
                          <FiPhone className="mr-2" />
                          Phone
                        </h3>
                        <p className="mt-1 text-gray-800">
                          {restaurantDetails?.contactInformation?.phone}
                        </p>
                      </div>

                      {restaurantDetails?.contactInformation?.email && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-500 flex items-center">
                            <FiMail className="mr-2" />
                            Email
                          </h3>
                          <p className="mt-1 text-gray-800">
                            {restaurantDetails?.contactInformation?.email}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Quick Stats Card */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <FiMonitor className="mr-2 text-yellow-500" />
                      Digital Presence
                    </h2>

                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-500">
                          Screens
                        </span>
                        <span className="font-medium text-gray-800">
                          {restaurantDetails?.screens?.length || 0}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-500">
                          Active Campaigns
                        </span>
                        <span className="font-medium text-gray-800">
                          {restaurantDetails?.screens?.reduce(
                            (acc, screen) =>
                              acc + (screen.campaigns?.length || 0),
                            0
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Screens Tab Content */
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                  <FiMonitor className="mr-2 text-yellow-500" />
                  Fixed Location Screens
                </h2>

                {restaurantDetails?.screens?.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {restaurantDetails.screens.map((screen) => (
                      <div
                        key={screen._id}
                        className="bg-white rounded-lg shadow overflow-hidden"
                      >
                        <div className="p-6">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-800">
                                {screen.model}
                              </h3>
                              <p className="text-sm text-gray-500">
                                {screen.uuid}
                              </p>
                            </div>
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                screen.onlineStatus
                              )}`}
                            >
                              {screen.onlineStatus}
                            </span>
                          </div>

                          <div className="mt-4 space-y-2">
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-500">
                                IMEI
                              </span>
                              <span className="text-sm font-medium text-gray-800">
                                {screen.imeiNumber}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-500">
                                Orientation
                              </span>
                              <span className="text-sm font-medium text-gray-800 capitalize">
                                {screen.defaultOrientation}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-500">
                                Status
                              </span>
                              <span className="text-sm font-medium text-gray-800 capitalize">
                                {screen.status}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <Link
                                to={`/screendetails/${screen._id}`}
                                className="ml-auto inline-flex items-center justify-center px-3 py-1 text-sm md:text-base font-semibold text-white bg-yellow-500 hover:bg-yellow-600 rounded-lg shadow transition duration-200 float-right"
                              >
                                View
                              </Link>
                            </div>
                          </div>
                        </div>

                        {/* Campaigns Section */}
                        {screen.campaigns?.length > 0 && (
                          <div className="border-t border-gray-200 p-6">
                            <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                              <FaCampground className="mr-2 text-yellow-500" />
                              Active Campaigns ({screen.campaigns.length})
                            </h4>

                            <div className="space-y-3">
                              {screen.campaigns.map((campaign) => (
                                <div
                                  key={campaign._id}
                                  className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
                                >
                                  <div>
                                    <p className="text-sm font-medium text-gray-800">
                                      {campaign.name}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {campaign.status}
                                    </p>
                                  </div>
                                  <Link
                                    to={`/campaigndetails/${campaign._id}`}
                                    className="text-xs font-medium text-yellow-600 hover:text-yellow-700"
                                  >
                                    View
                                  </Link>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow p-12 text-center">
                    <FiMonitor className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      No screens found
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      This restaurant doesn't have any screens assigned yet.
                    </p>
                    <div className="mt-6">
                      <Link
                        to="/screens"
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                      >
                        <FiPlus className="-ml-1 mr-2 h-5 w-5" />
                        Add New Screen
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </AdminLayout>
  );
}

export default RestaurantDetails;
